import React, { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, RotateCcw, ArrowRight, Ear, Hand } from "lucide-react";
import { playDrum, unlockAudio } from "../lib/audio";
import { PADS } from "../lib/drums";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERVALS = [750, 950, 1150];

const DIFFICULTIES = [
  { id: "gentle", label: "Gentle", beats: 3, note: "3 beats · drums only", pads: ["djembe", "conga", "tom"] },
  { id: "steady", label: "Steady", beats: 4, note: "4 beats · adds the slap", pads: ["djembe", "conga", "tom", "slap"] },
  { id: "lively", label: "Lively", beats: 5, note: "5 beats · claps & shaker", pads: ["djembe", "conga", "tom", "slap", "clap", "shaker"] },
];

const speakCheer = async (text) => {
  try {
    const { data } = await axios.post(`${API}/tts`, { text });
    const a = new Audio(`data:audio/mp3;base64,${data.audio}`);
    a.volume = 0.82;
    a.play().catch(() => {});
  } catch (e) {
    /* voice is a bonus */
  }
};

function buildPattern(count, pool) {
  const ids = pool && pool.length ? pool : PADS.map((p) => p.id);
  let t = 0;
  const pat = [];
  for (let i = 0; i < count; i++) {
    if (i > 0) t += INTERVALS[Math.floor(Math.random() * INTERVALS.length)];
    pat.push({ padId: ids[Math.floor(Math.random() * ids.length)], t });
  }
  return pat;
}

export default function RhythmChallenge() {
  const [phase, setPhase] = useState("idle"); // idle | demo | listen | result
  const [difficulty, setDifficulty] = useState(null);
  const [level, setLevel] = useState(3);
  const [activePad, setActivePad] = useState(null);
  const [hitCount, setHitCount] = useState(0);
  const [result, setResult] = useState(null); // {accuracy, text, passed}

  const patternRef = useRef([]);
  const userHitsRef = useRef([]);
  const phaseRef = useRef("idle");
  const timeouts = useRef([]);
  const evalRef = useRef(null);
  const evaluatedRef = useRef(false);
  phaseRef.current = phase;

  const clearTimers = () => {
    timeouts.current.forEach((id) => clearTimeout(id));
    timeouts.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const beginListen = useCallback(() => {
    userHitsRef.current = [];
    evaluatedRef.current = false;
    setHitCount(0);
    setActivePad(null);
    setPhase("listen");
    // watchdog scales with the (slower) pattern so players aren't rushed
    const pat = patternRef.current;
    const span = pat.length ? pat[pat.length - 1].t : 3000;
    timeouts.current.push(
      setTimeout(() => {
        if (phaseRef.current === "listen" && evalRef.current) evalRef.current();
      }, span + 6000)
    );
  }, []);

  const demonstrate = useCallback((pattern) => {
    if (!pattern.length) return;
    setPhase("demo");
    setActivePad(null);
    pattern.forEach((beat) => {
      timeouts.current.push(
        setTimeout(() => {
          setActivePad(beat.padId);
          playDrum(beat.padId, { velocity: 1 });
          timeouts.current.push(setTimeout(() => setActivePad(null), 240));
        }, beat.t + 400)
      );
    });
    const total = pattern[pattern.length - 1].t + 400;
    timeouts.current.push(setTimeout(() => beginListen(), total + 800));
  }, [beginListen]);

  const startChallenge = useCallback((lvl, diff) => {
    const d = diff || difficulty || DIFFICULTIES[0];
    unlockAudio();
    clearTimers();
    setResult(null);
    const pattern = buildPattern(lvl ?? level, d.pads);
    patternRef.current = pattern;
    demonstrate(pattern);
  }, [level, difficulty, demonstrate]);

  const chooseDifficulty = useCallback((d) => {
    setDifficulty(d);
    setLevel(d.beats);
    startChallenge(d.beats, d);
  }, [startChallenge]);

  const evaluate = useCallback(() => {
    if (evaluatedRef.current) return;
    evaluatedRef.current = true;
    clearTimers();
    const pat = patternRef.current;
    const hits = userHitsRef.current;
    let correctOrder = 0;
    for (let i = 0; i < pat.length; i++) {
      if (hits[i] && hits[i].padId === pat[i].padId) correctOrder++;
    }
    const orderScore = correctOrder / pat.length;

    const patOff = pat.map((b) => b.t - pat[0].t);
    const userOff = hits.map((h) => h.at - hits[0].at);
    let timingSum = 0;
    for (let i = 0; i < pat.length; i++) {
      const err = Math.abs((patOff[i] || 0) - (userOff[i] || 0));
      // forgiving: ignore the first ~300ms of drift, then a very gentle falloff
      timingSum += Math.max(0, 1 - Math.max(0, err - 300) / 1000);
    }
    const timingScore = timingSum / pat.length;
    // getting the drums in the right order matters most; timing is a light bonus
    let accuracy = Math.round(100 * (0.85 * orderScore + 0.15 * timingScore));
    // reward keeping the pattern even if the pace was off
    if (orderScore >= 0.999) accuracy = Math.max(accuracy, 92);
    else if (orderScore >= 0.66) accuracy = Math.max(accuracy, 74);

    let text, cheer, passed;
    if (accuracy >= 88) {
      text = "Beautifully done.";
      cheer = "Beautifully done — you felt every beat.";
      passed = true;
    } else if (accuracy >= 60) {
      text = "Lovely — you kept the rhythm.";
      cheer = "Lovely. You kept the rhythm — well done.";
      passed = true;
    } else if (accuracy >= 35) {
      text = "So close — feel it once more.";
      cheer = "So close. Trust yourself, and feel it once more.";
      passed = false;
    } else {
      text = "Gently, once more.";
      cheer = "That's okay. Take a breath, and we'll try again.";
      passed = false;
    }
    setResult({ accuracy, text, passed });
    setPhase("result");
    speakCheer(cheer);
  }, []);
  evalRef.current = evaluate;

  const onPad = useCallback((pad) => {
    if (phaseRef.current !== "listen") return;
    unlockAudio();
    const now = performance.now();
    userHitsRef.current.push({ padId: pad.id, at: now });
    setHitCount(userHitsRef.current.length);
    setActivePad(pad.id);
    playDrum(pad.id, { velocity: 1 });
    setTimeout(() => setActivePad((p) => (p === pad.id ? null : p)), 150);
    if (userHitsRef.current.length >= patternRef.current.length) {
      setTimeout(() => evaluate(), 120);
    }
  }, [evaluate]);

  const nextPattern = useCallback(() => {
    const nl = level + 1;
    setLevel(nl);
    startChallenge(nl, difficulty);
  }, [level, difficulty, startChallenge]);

  const patternLen = patternRef.current.length || level;

  const poolIds = difficulty ? difficulty.pads : PADS.map((p) => p.id);
  const shownPads = PADS.filter((p) => poolIds.includes(p.id));

  const status =
    phase === "demo" ? "Watch & listen…" :
    phase === "listen" ? "Your turn — play it back" :
    phase === "idle" ? "Ready when you are." : "";

  return (
    <section
      id="challenge"
      className="relative py-24 md:py-32 px-6 md:px-10 border-t border-white/5 overflow-hidden"
      data-testid="rhythm-challenge-section"
    >
      <div
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(55% 55% at 30% 20%, rgba(var(--glow),0.14), transparent 65%)" }}
      />
      <div className="relative max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10 bg-primary-c/70" />
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-primary-c">Call &amp; Response</p>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl leading-[1.04]">
          Feel the moment it clicks.
        </h2>
        <p className="mt-4 text-fg/60 md:text-lg max-w-2xl">
          Listen, then play it back. There&rsquo;s no failing here — just the quiet relief of hands and mind moving in sync, and the calm that follows. We&rsquo;ll gently show how closely you matched the beat.
        </p>

        {/* status + progress */}
        <div className="mt-10 flex items-center gap-4 min-h-[32px]" data-testid="challenge-status-row">
          <span
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase"
            style={{ color: phase === "listen" ? "var(--primary)" : "rgba(245,240,255,0.6)" }}
            data-testid="challenge-status"
            aria-live="polite"
          >
            {phase === "demo" && <Ear size={14} />}
            {phase === "listen" && <Hand size={14} />}
            {status}
          </span>
          {(phase === "demo" || phase === "listen") && (
            <div className="flex items-center gap-1.5" data-testid="challenge-progress">
              {Array.from({ length: patternLen }).map((_, i) => (
                <span
                  key={i}
                  className="h-2 w-2 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: i < hitCount ? "var(--primary)" : "rgba(255,255,255,0.15)",
                    boxShadow: i < hitCount ? "0 0 10px rgba(var(--glow),0.7)" : "none",
                  }}
                />
              ))}
            </div>
          )}
          <span className="ml-auto font-mono text-[11px] tracking-[0.2em] uppercase text-fg/40" data-testid="challenge-level">
            {difficulty ? `${difficulty.label} · ${level} beats` : `Level ${level}`}
          </span>
        </div>

        {/* pads (limited to the chosen difficulty's instruments) */}
        <div className="mt-6 grid gap-3 md:gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))" }}>
          {shownPads.map((pad) => {
            const on = activePad === pad.id;
            const playable = phase === "listen";
            return (
              <motion.button
                key={pad.id}
                data-testid={`challenge-pad-${pad.id}`}
                onPointerDown={() => onPad(pad)}
                whileTap={playable ? { scale: 0.93 } : {}}
                disabled={!playable}
                className="shape-r relative overflow-hidden py-8 md:py-10 px-4 font-mono text-xs tracking-[0.2em] uppercase glass text-fg/90 transition-all duration-150"
                style={{
                  border: `1px solid ${pad.color}${on ? "" : "55"}`,
                  backgroundColor: on ? `rgba(${pad.rgb},0.22)` : undefined,
                  boxShadow: on ? `0 0 40px rgba(${pad.rgb},0.3), inset 0 0 26px rgba(${pad.rgb},0.16)` : "inset 0 1px 0 rgba(255,255,255,0.06)",
                  opacity: !playable && phase !== "idle" && phase !== "result" ? 0.85 : 1,
                  cursor: playable ? "pointer" : "default",
                }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: pad.color, boxShadow: `0 0 12px ${pad.color}` }} />
                  {pad.label}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* controls / result */}
        <div className="mt-10 min-h-[120px]">
          <AnimatePresence mode="wait">
            {phase === "idle" && (
              <motion.div
                key="difficulties"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="flex flex-col gap-4"
                data-testid="difficulty-callouts"
              >
                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-fg/45">Choose your pace</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {DIFFICULTIES.map((d) => (
                    <button
                      key={d.id}
                      data-testid={`difficulty-${d.id}`}
                      onClick={() => chooseDifficulty(d)}
                      className="group text-left shape-r glass p-5 border border-white/10 hover:border-primary-c/60 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-serif text-xl">{d.label}</span>
                        <Play size={15} className="text-primary-c opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-fg/50">{d.note}</span>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {phase === "result" && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="glass shape-r p-8 flex flex-col sm:flex-row sm:items-center gap-6"
                data-testid="challenge-result"
              >
                <div className="flex items-center gap-5">
                  <div className="relative grid place-items-center h-20 w-20 shrink-0">
                    <svg viewBox="0 0 36 36" className="h-20 w-20 -rotate-90">
                      <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="16" fill="none"
                        stroke={result.passed ? "var(--primary)" : "#b89556"}
                        strokeWidth="3" strokeLinecap="round"
                        strokeDasharray={`${(result.accuracy / 100) * 100.5} 100.5`}
                      />
                    </svg>
                    <span className="absolute font-serif text-xl" data-testid="challenge-accuracy">{result.accuracy}%</span>
                  </div>
                  <div>
                    <p className="font-serif text-2xl md:text-3xl leading-snug" data-testid="challenge-result-text">{result.text}</p>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-fg/50 mt-2">Accuracy · drums + order + timing</p>
                  </div>
                </div>
                <div className="sm:ml-auto flex gap-3">
                  {result.passed ? (
                    <button
                      onClick={nextPattern}
                      data-testid="challenge-next-btn"
                      className="inline-flex items-center gap-2 shape-r bg-primary-c text-bg font-medium px-6 py-3 hover:opacity-90 transition-opacity"
                    >
                      Next pattern <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={() => startChallenge()}
                      data-testid="challenge-retry-btn"
                      className="inline-flex items-center gap-2 shape-r border border-white/25 px-6 py-3 font-medium hover:bg-white/10 transition-colors"
                    >
                      <RotateCcw size={16} /> Try again
                    </button>
                  )}
                  <button
                    onClick={() => { clearTimers(); setResult(null); setPhase("idle"); }}
                    data-testid="challenge-change-difficulty"
                    className="inline-flex items-center gap-2 shape-r border border-white/15 px-5 py-3 font-medium text-fg/70 hover:bg-white/10 transition-colors"
                  >
                    Change pace
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
